import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, Outlet, useLocation } from "react-router-dom";
import {
  Home, Search, Briefcase, MessageCircle, User, Bell, Plus, LogOut, Shield, Menu,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { LogoMark } from "@/components/Logo";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

function ModeSwitch() {
  const { mode, setMode } = useAuth();
  return (
    <div className="inline-flex rounded-full bg-slate-100 p-1 shadow-inner" data-testid="mode-switch">
      <button
        data-testid="mode-worker"
        onClick={() => setMode("worker")}
        className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
          mode === "worker" ? "bg-blue-600 text-white" : "text-slate-500 hover:text-slate-800"
        }`}
      >
        Cari Kerja
      </button>
      <button
        data-testid="mode-employer"
        onClick={() => setMode("employer")}
        className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
          mode === "employer" ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-800"
        }`}
      >
        Cari Tenaga
      </button>
    </div>
  );
}

export function AppLayout() {
  const { user, mode, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [unread, setUnread] = useState(0);
  const [chatUnread, setChatUnread] = useState(0);

  const loadUnread = async () => {
    try {
      const { data } = await api.get("/notifications");
      setUnread(data.unread);
    } catch {}
  };

  const loadChatUnread = async () => {
    try {
      const { data } = await api.get("/chats");
      setChatUnread(data.reduce((s, c) => s + (c.unread || 0), 0));
    } catch {}
  };

  useEffect(() => {
    loadUnread();
    loadChatUnread();
    const t = setInterval(() => { loadUnread(); loadChatUnread(); }, 20000);
    return () => clearInterval(t);
  }, [location.pathname]);

  useEffect(() => {
    const token = localStorage.getItem("andil_token");
    if (!token) return;
    const wsBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws");
    let ws;
    try {
      ws = new WebSocket(`${wsBase}/api/ws/user?token=${token}`);
      ws.onmessage = (ev) => {
        try {
          const d = JSON.parse(ev.data);
          if (d.type === "notification") {
            setUnread((u) => u + 1);
            const note = d.notification || {};
            if (note.type === "new_message") loadChatUnread();
            toast(note.message || "Notifikasi baru", note.job_id ? {
              action: { label: "Buka", onClick: () => navigate(`/pekerjaan/${note.job_id}`) },
            } : undefined);
          }
        } catch {}
      };
    } catch {}
    return () => { try { ws && ws.close(); } catch {} };
  }, []);

  const workerTabs = [
    { to: "/app", icon: Home, label: "Beranda", end: true },
    { to: "/cari", icon: Search, label: "Cari Kerja" },
    { to: "/pekerjaan-saya", icon: Briefcase, label: "Kerjaan" },
    { to: "/pesan", icon: MessageCircle, label: "Pesan" },
    { to: "/profil", icon: User, label: "Profil" },
  ];
  const employerTabs = [
    { to: "/app", icon: Home, label: "Beranda", end: true },
    { to: "/pekerjaan-saya", icon: Briefcase, label: "Kerjaan" },
    { to: "/buat", icon: Plus, label: "Buat", primary: true },
    { to: "/pesan", icon: MessageCircle, label: "Pesan" },
    { to: "/profil", icon: User, label: "Profil" },
  ];
  const tabs = mode === "employer" ? employerTabs : workerTabs;

  const initials = (user?.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between gap-3">
          <button data-testid="logo-btn" onClick={() => navigate("/app")} className="flex items-center gap-2 shrink-0">
            <LogoMark size={36} rounded="rounded-lg" />
            <span className="font-extrabold text-slate-900 text-lg tracking-tight hidden sm:block">ANDIL</span>
          </button>

          <nav className="hidden md:flex items-center gap-1">
            {tabs.filter((t) => !t.primary).map((t) => (
              <NavLink
                key={t.to}
                to={t.to}
                end={t.end}
                data-testid={`nav-desktop-${t.label}`}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${
                    isActive ? "text-blue-600 bg-blue-50" : "text-slate-600 hover:bg-slate-100"
                  }`
                }
              >
                {t.label}
                {t.label === "Pesan" && chatUnread > 0 && (
                  <span data-testid="nav-chat-badge" className="ml-1.5 min-w-4 h-4 px-1 bg-rose-500 text-white text-[10px] font-bold rounded-full inline-flex items-center justify-center align-middle">{chatUnread > 9 ? "9+" : chatUnread}</span>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <div className="hidden sm:block"><ModeSwitch /></div>
            {mode === "employer" && (
              <button
                data-testid="header-create-btn"
                onClick={() => navigate("/buat")}
                className="hidden md:inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-3 py-2 rounded-lg transition-colors"
              >
                <Plus size={16} /> Buat
              </button>
            )}
            <button
              data-testid="notif-btn"
              onClick={() => navigate("/notifikasi")}
              className="relative w-10 h-10 rounded-full hover:bg-slate-100 flex items-center justify-center transition-colors"
            >
              <Bell size={20} className="text-slate-700" />
              {unread > 0 && (
                <span data-testid="notif-badge" className="absolute top-1.5 right-1.5 min-w-4 h-4 px-1 bg-rose-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                  {unread > 9 ? "9+" : unread}
                </span>
              )}
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="avatar-menu-btn" className="outline-none">
                  <Avatar className="w-9 h-9 border border-slate-200">
                    <AvatarImage src={fileUrl(user?.photo_url)} />
                    <AvatarFallback className="bg-blue-100 text-blue-700 font-bold text-sm">{initials}</AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <div className="px-2 py-1.5">
                  <p className="font-semibold text-slate-900 text-sm truncate">{user?.name}</p>
                  <p className="text-xs text-slate-400 truncate">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <div className="sm:hidden px-2 py-1.5"><ModeSwitch /></div>
                <DropdownMenuItem data-testid="menu-profile" onClick={() => navigate("/profil")}>
                  <User size={16} className="mr-2" /> Profil
                </DropdownMenuItem>
                {user?.role === "admin" && (
                  <DropdownMenuItem data-testid="menu-admin" onClick={() => navigate("/admin")}>
                    <Shield size={16} className="mr-2" /> Admin
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-logout" onClick={async () => { await logout(); navigate("/"); }}>
                  <LogOut size={16} className="mr-2" /> Keluar
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6 pb-28 md:pb-10">
        <Outlet />
      </main>

      {/* Mobile bottom nav */}
      <nav className="fixed bottom-0 inset-x-0 z-40 bg-white border-t border-slate-200 md:hidden pb-safe">
        <div className="flex items-center justify-around px-2 pt-1.5">
          {tabs.map((t) => (
            <NavLink
              key={t.to}
              to={t.to}
              end={t.end}
              data-testid={`nav-mobile-${t.label}`}
              className={({ isActive }) =>
                t.primary
                  ? "flex flex-col items-center -mt-5"
                  : `flex flex-col items-center gap-0.5 py-1.5 px-2 text-[11px] font-medium transition-colors ${
                      isActive ? "text-blue-600" : "text-slate-400"
                    }`
              }
            >
              {t.primary ? (
                <>
                  <span className="w-12 h-12 rounded-2xl bg-blue-600 text-white flex items-center justify-center shadow-lg">
                    <t.icon size={24} />
                  </span>
                </>
              ) : (
                <>
                  <span className="relative">
                    <t.icon size={21} />
                    {t.label === "Pesan" && chatUnread > 0 && (
                      <span data-testid="nav-chat-badge-mobile" className="absolute -top-1.5 -right-2 min-w-4 h-4 px-1 bg-rose-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">{chatUnread > 9 ? "9+" : chatUnread}</span>
                    )}
                  </span>
                  <span>{t.label}</span>
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
